import { useMemo } from 'react';
import { TableRow, TableCell } from '../ui/Table';
import { StatusChip } from './StatusChip';

/**
 * Statement-line row used by the reconciliation workbench tables.
 *
 * Responsibilities (plan §8):
 *  - Consistent row format across the Queue / Suggested / Matched / Ignored /
 *    Reconciled / All tabs.
 *  - Highlights outflows in red and inflows in green (money-sign).
 *  - Renders a checkbox for bulk selection when onToggleSelect is provided.
 *  - Offers quick "Match" / "Split" / "Ignore" actions. The parent chooses
 *    which actions to enable via the `actions` prop.
 */
export function StatementLineRow({
  line,
  selected,
  onToggleSelect,
  onMatch,
  onSplit,
  onTransfer,
  onLoanRepayment,
  onLoanDrawdown,
  onCreditCardSettlement,
  onDrawings,
  onIgnore,
  onUnignore,
  onViewMatch,
  showActions = true,
}) {
  const amount = Number(line?.amount ?? 0);
  const amountDisplay = useMemo(() => {
    if (!Number.isFinite(amount)) return '—';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
    }).format(amount);
  }, [amount]);

  const dateDisplay = line?.transaction_date
    ? new Date(line.transaction_date).toISOString().slice(0, 10)
    : '—';

  const amountClass =
    amount > 0
      ? 'text-green-700 dark:text-green-400 font-medium'
      : amount < 0
        ? 'text-red-700 dark:text-red-400 font-medium'
        : 'text-gray-600 dark:text-gray-300';

  const status = line?.status || 'unmatched';

  return (
    <TableRow>
      {onToggleSelect && (
        <TableCell className="w-8">
          <input
            type="checkbox"
            checked={!!selected}
            onChange={(e) => onToggleSelect(line.id, e.target.checked)}
            aria-label={`Select line ${line.line_number ?? line.id}`}
          />
        </TableCell>
      )}
      <TableCell>{dateDisplay}</TableCell>
      <TableCell className="max-w-[280px] truncate" title={line?.description || ''}>
        {line?.description || '—'}
      </TableCell>
      <TableCell className="max-w-[160px] truncate" title={line?.reference || ''}>
        {line?.reference || '—'}
      </TableCell>
      <TableCell className="max-w-[200px] truncate" title={line?.counterparty || ''}>
        {line?.counterparty || '—'}
      </TableCell>
      <TableCell align="right" className={amountClass}>
        {amountDisplay}
      </TableCell>
      <TableCell>
        <StatusChip status={status} />
      </TableCell>
      {showActions && (
        <TableCell className="whitespace-nowrap">
          <div className="flex gap-2">
            {['unmatched', 'suggested', 'partially_matched', 'needs_review'].includes(status) && onMatch && (
              <button
                type="button"
                onClick={() => onMatch(line)}
                className="text-xs text-blue-600 hover:underline dark:text-blue-400"
              >
                Match
              </button>
            )}
            {['unmatched', 'suggested', 'partially_matched', 'needs_review'].includes(status) && onSplit && (
              <button
                type="button"
                onClick={() => onSplit(line)}
                className="text-xs text-blue-600 hover:underline dark:text-blue-400"
              >
                Split
              </button>
            )}
            {['unmatched', 'suggested', 'partially_matched', 'needs_review'].includes(status) && onTransfer && (
              <button
                type="button"
                onClick={() => onTransfer(line)}
                className="text-xs text-blue-600 hover:underline dark:text-blue-400"
              >
                Transfer
              </button>
            )}
            {['unmatched', 'suggested', 'partially_matched', 'needs_review'].includes(status) && onLoanRepayment && (
              <button
                type="button"
                onClick={() => onLoanRepayment(line)}
                className="text-xs text-purple-600 hover:underline dark:text-purple-400"
                title="Match to a loan repayment schedule line"
              >
                Loan pmt
              </button>
            )}
            {['unmatched', 'suggested', 'partially_matched', 'needs_review'].includes(status) && onLoanDrawdown && (
              <button
                type="button"
                onClick={() => onLoanDrawdown(line)}
                className="text-xs text-purple-600 hover:underline dark:text-purple-400"
                title="Match this deposit as a loan drawdown"
              >
                Drawdown
              </button>
            )}
            {['unmatched', 'suggested', 'partially_matched', 'needs_review'].includes(status) && onCreditCardSettlement && (
              <button
                type="button"
                onClick={() => onCreditCardSettlement(line)}
                className="text-xs text-amber-700 hover:underline dark:text-amber-400"
                title="Match this as a credit-card settlement (bank ↔ card payment)"
              >
                CC settle
              </button>
            )}
            {['unmatched', 'suggested', 'partially_matched', 'needs_review'].includes(status) && onDrawings && (
              <button
                type="button"
                onClick={() => onDrawings(line)}
                className="text-xs text-emerald-700 hover:underline dark:text-emerald-400"
                title="Record this as a proprietor-drawings event (transfer / personal spend / reimbursement)"
              >
                Drawings
              </button>
            )}
            {['matched', 'reconciled', 'partially_matched'].includes(status) && onViewMatch && (
              <button
                type="button"
                onClick={() => onViewMatch(line)}
                className="text-xs text-blue-600 hover:underline dark:text-blue-400"
              >
                View
              </button>
            )}
            {['unmatched', 'needs_review', 'suggested'].includes(status) && onIgnore && (
              <button
                type="button"
                onClick={() => onIgnore(line)}
                className="text-xs text-gray-500 hover:underline dark:text-gray-400"
              >
                Ignore
              </button>
            )}
            {status === 'ignored' && onUnignore && (
              <button
                type="button"
                onClick={() => onUnignore(line)}
                className="text-xs text-gray-500 hover:underline dark:text-gray-400"
              >
                Un-ignore
              </button>
            )}
          </div>
        </TableCell>
      )}
    </TableRow>
  );
}

export default StatementLineRow;
